import { useEffect, useState } from "react";
import { api, MXN, MONTH_NAMES, TRAMITES } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Users, ChevronRight, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import ClientDetailModal from "@/components/ClientDetailModal";

const YEARS = Array.from({ length: 11 }, (_, i) => 2026 + i);
const GOLD = "#D4AF37";
const GOLD_DARK = "#B8912B";

export default function Statistics() {
  const currentYear = new Date().getFullYear();
  const initialYear = YEARS.includes(currentYear) ? currentYear : 2026;
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    setSummary(null);
    try {
      const params = { year };
      if (month) params.month = month;
      const r = await api.get("/stats/summary", { params });
      setSummary(r.data);
    } catch {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  return (
    <section data-testid="estadisticas-section">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-neutral-900">Estadísticas</h1>
        <p className="text-neutral-500 mt-1 text-sm">Elige un año y explora tus ingresos esperados por mes y los clientes de cada mes.</p>
      </div>

      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-2">Año</div>
        <div className="flex flex-wrap gap-2">
          {YEARS.map((y) => (
            <button
              key={y}
              data-testid={`year-${y}`}
              onClick={() => { setYear(y); setMonth(null); }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                y === year
                  ? "bg-black text-yellow-400 border-black shadow"
                  : "bg-white text-neutral-700 border-neutral-200 hover:border-yellow-500 hover:text-black"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4">
        <button
          onClick={() => setMonth(null)}
          className={`font-semibold ${!month ? "text-black" : "hover:text-black"}`}
        >{year}</button>
        {month && (
          <>
            <ChevronRight className="w-4 h-4" />
            <span className="text-black font-semibold">{MONTH_NAMES[month - 1]}</span>
          </>
        )}
      </div>

      {loading && !summary && <div className="text-neutral-500 text-sm">Cargando…</div>}

      {summary && !month && (
        <YearView summary={summary} year={year} onSelectMonth={(m) => setMonth(m)} />
      )}

      {summary && month && summary.days && (
        <MonthView
          summary={summary}
          month={month}
          year={year}
          onOpenClient={(id) => setDetailId(id)}
        />
      )}

      {detailId && (
        <ClientDetailModal
          clientId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </section>
  );
}

function StatBanner({ label, value, icon, mono }) {
  return (
    <div className="rounded-2xl border border-yellow-500/40 p-5 bg-gradient-to-br from-black to-neutral-900 text-yellow-400 shadow-lg">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <div className={`mt-2 text-3xl font-heading font-extrabold ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function YearView({ summary, year, onSelectMonth }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatBanner
          label="Clientes registrados en el año"
          value={summary.total_registered_year}
          icon={<Users className="w-5 h-5" />}
        />
        <StatBanner
          label="Ingresos esperados en el año"
          value={MXN(summary.total_revenue_year)}
          icon={<TrendingUp className="w-5 h-5" />}
          mono
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-6">
        <h3 className="font-heading font-semibold text-neutral-800 mb-3">Ingresos por mes — {year}</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={summary.months.map((m) => ({ name: MONTH_NAMES[m.month - 1].slice(0, 3), Ingresos: m.revenue }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => MXN(v)} />
              <Bar dataKey="Ingresos" fill={GOLD} stroke={GOLD_DARK} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {MONTH_NAMES.map((m, i) => {
          const info = summary.months?.[i];
          return (
            <button
              key={m}
              data-testid={`month-${i + 1}`}
              onClick={() => onSelectMonth(i + 1)}
              className="bg-white border border-neutral-200 rounded-2xl p-4 text-left hover:border-yellow-500 hover:shadow-md transition"
            >
              <div className="text-xs uppercase tracking-wider text-neutral-400">Mes</div>
              <div className="font-heading text-lg font-bold text-black">{m}</div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-neutral-500">{info?.registered ?? 0} clientes</span>
                <span className="text-yellow-700 font-mono font-semibold">{MXN(info?.revenue || 0)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function MonthView({ summary, month, year, onOpenClient }) {
  const clients = summary.clients || [];
  const monthName = MONTH_NAMES[month - 1];
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatBanner
          label={`Ingresos esperados en ${monthName}`}
          value={MXN(summary.month_total_revenue)}
          icon={<TrendingUp className="w-5 h-5" />}
          mono
        />
        <StatBanner
          label={`Clientes con cobro en ${monthName}`}
          value={clients.length}
          icon={<Users className="w-5 h-5" />}
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-6" data-testid="month-revenue-chart">
        <h3 className="font-heading font-semibold text-neutral-800 mb-3">Ingresos por día — {monthName} {year}</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={summary.days.map((d) => ({ name: String(d.day), Ingresos: d.revenue }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => MXN(v)} labelFormatter={(l) => `${l} de ${monthName}`} />
              <Bar dataKey="Ingresos" fill={GOLD} stroke={GOLD_DARK} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="month-clients-list">
        <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-neutral-800">Clientes de {monthName}</h3>
          <span className="text-xs text-neutral-500">{clients.length} cliente{clients.length !== 1 ? "s" : ""}</span>
        </div>
        {clients.length === 0 && (
          <div className="text-center py-10 text-neutral-400 text-sm">Sin clientes con fecha de ejecución en este mes.</div>
        )}
        <div className="divide-y divide-neutral-100">
          {clients.map((c) => {
            const t = c.tramite ? TRAMITES[c.tramite] : null;
            return (
              <div key={c.id} data-testid={`month-client-${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-yellow-50/40 transition">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900 truncate">{c.nombre_completo}</span>
                    {t ? (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: `${t.color}12`, color: t.color, borderColor: `${t.color}55` }}
                      >{t.label}</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-dashed border-neutral-300 text-neutral-400">Sin trámite</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono mt-1">
                    NSS {c.nss} · CURP {c.curp} · {c.zona}
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">Fecha de ejecución</div>
                  <div className="text-sm text-neutral-800">{fmt(c.fecha_ejecucion)}</div>
                </div>
                <div className="text-right min-w-[110px]">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">Cantidad</div>
                  <div className="font-mono font-semibold text-yellow-700">{MXN(c.cobro)}</div>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onOpenClient(c.id)}
                  data-testid={`month-view-${c.id}`}
                  className="rounded-full h-9 w-9 border-neutral-300 hover:bg-yellow-50 hover:border-yellow-500 hover:text-black"
                  title="Ver detalle"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
